import React from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, TextField, Typography,
} from '@mui/material';
import {
  FACEBOOK_FOLLOWER_OPTIONS,
  FACEBOOK_ACCOUNT_AGE_OPTIONS,
  INSTAGRAM_FOLLOWER_OPTIONS,
  INSTAGRAM_ACCOUNT_AGE_OPTIONS,
  LINKEDIN_FOLLOWER_OPTIONS,
} from './webPresenceAssessmentOptions';

export const STRUCTURED_TYPES = new Set(['FACEBOOK', 'INSTAGRAM', 'LINKEDIN']);

export const EMPTY_STRUCTURED_STATE = {
  fb_follower_bucket: '',
  fb_account_age_bucket: '',
  ig_follower_bucket: '',
  ig_account_age_bucket: '',
  li_follower_bucket: '',
  li_member_count: '',
  li_range_start: '',
  li_range_end: '',
};

export const AssessmentDropdown = ({ label, options, value, onChange }) => (
  <FormControl size="small" fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select value={value} label={label} onChange={(e) => onChange(e.target.value)}>
      {options.map((opt) => (
        <MenuItem key={opt.value || '__skip__'} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

export const buildStructuredInputs = (type, state) => {
  if (type === 'FACEBOOK') {
    return {
      fb_follower_bucket: state.fb_follower_bucket || null,
      fb_account_age_bucket: state.fb_account_age_bucket || null,
    };
  }
  if (type === 'INSTAGRAM') {
    return {
      ig_follower_bucket: state.ig_follower_bucket || null,
      ig_account_age_bucket: state.ig_account_age_bucket || null,
    };
  }
  if (type === 'LINKEDIN') {
    const inputs = { li_follower_bucket: state.li_follower_bucket || null };
    const { li_member_count, li_range_start, li_range_end } = state;
    if (li_member_count !== '' && li_range_start !== '' && li_range_end !== '') {
      inputs.li_discrepancy = {
        member_count: Number(li_member_count),
        range_start: Number(li_range_start),
        range_end: Number(li_range_end),
      };
    }
    return inputs;
  }
  return {};
};

export const hasAnyStructuredInput = (inputs) =>
  Object.values(inputs).some((v) => v !== null && v !== undefined && v !== '');

export const StructuredAssessmentFields = ({ type, state, onChange }) => {
  const update = (key) => (v) => onChange({ ...state, [key]: v });

  if (type === 'FACEBOOK') {
    return (
      <>
        <AssessmentDropdown
          label="Follower count"
          options={FACEBOOK_FOLLOWER_OPTIONS}
          value={state.fb_follower_bucket}
          onChange={update('fb_follower_bucket')}
        />
        <AssessmentDropdown
          label="Account age"
          options={FACEBOOK_ACCOUNT_AGE_OPTIONS}
          value={state.fb_account_age_bucket}
          onChange={update('fb_account_age_bucket')}
        />
      </>
    );
  }
  if (type === 'INSTAGRAM') {
    return (
      <>
        <AssessmentDropdown
          label="Follower count"
          options={INSTAGRAM_FOLLOWER_OPTIONS}
          value={state.ig_follower_bucket}
          onChange={update('ig_follower_bucket')}
        />
        <AssessmentDropdown
          label="Account age"
          options={INSTAGRAM_ACCOUNT_AGE_OPTIONS}
          value={state.ig_account_age_bucket}
          onChange={update('ig_account_age_bucket')}
        />
      </>
    );
  }
  if (type === 'LINKEDIN') {
    return (
      <>
        <AssessmentDropdown
          label="Follower count"
          options={LINKEDIN_FOLLOWER_OPTIONS}
          value={state.li_follower_bucket}
          onChange={update('li_follower_bucket')}
        />
        <Typography variant="caption" color="text.secondary">
          Employee count discrepancy — fill all three to compute, or leave blank to skip.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label="LinkedIn members"
            type="number"
            value={state.li_member_count}
            onChange={(e) => update('li_member_count')(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Reported min"
            type="number"
            value={state.li_range_start}
            onChange={(e) => update('li_range_start')(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Reported max"
            type="number"
            value={state.li_range_end}
            onChange={(e) => update('li_range_end')(e.target.value)}
            fullWidth
          />
        </Box>
      </>
    );
  }
  return null;
};
